import React, { useEffect } from "react";
import { useNavigate } from "react-router";
import { Button, Modal } from "@accessdev/arc-ui-react";
import { Trans } from "@lingui/react/macro";
import { ROUTES } from "@Src/constants";
import { pingSession, keepAliveSession } from "@Src/api/sessionApi";

import "./MonitorUserTimeout.scss";

export default function MonitorUserTimeout() {
  const [showWarning, setShowWarning] = React.useState(false);
  const [count, setCount] = React.useState(0);
  const navigate = useNavigate();
  const second = 1000;
  const minute = 60 * second;
  const initialDuration = 55 * minute;
  const countdownLeftRef = React.useRef(initialDuration);
  const idleDuration = 5 * minute;

  const logout = React.useCallback(() => {
    setCount(0);
    setShowWarning(false);
    navigate(ROUTES.SIGNOUT_PATH);
  }, [navigate, setShowWarning]);

  useEffect(() => {
    let interval = null;

    // Start the countdown timer if showWarning is false
    if (!showWarning) {
      interval = setInterval(() => {
        countdownLeftRef.current -= second;
        // If the countdown reaches 0, show the warning and reset the countdown
        if (countdownLeftRef.current === 0) {
          setShowWarning(true);
          clearInterval(interval);
          countdownLeftRef.current = initialDuration;
        }
      }, 1000);
    }

    // Clean up the interval when the component unmounts or showWarning changes
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [minute, showWarning, countdownLeftRef, initialDuration]);

  // Start the countdown timer if showWarning is true
  useEffect(() => {
    let countDown = null;
    if (showWarning) {
      // Increment the count every second
      countDown = setInterval(async () => {
        setCount(count + 1);
      }, 1000);
    }

    // Clean up the interval when the component unmounts or showWarning changes
    return () => {
      if (countDown) clearInterval(countDown);
    };
  }, [showWarning, count]);

  useEffect(() => {
    // if 1 minute duration passes, fire the signout flow
    if (count >= 60) {
      logout();
    }
  }, [count, logout]);

  // Add event listener for mouse over event
  useEffect(() => {
    // Set up a timeout interval to periodically ping the session
    const timeout = setInterval(async function () {
      const res = await pingSession();
      // If the session is about to expire, show the warning
      if (res.status === "WARN") {
        setShowWarning(true);
      }
    }, idleDuration);

    // Clean up the timeout interval when the component unmounts
    return () => {
      clearInterval(timeout);
    };
  }, [initialDuration, idleDuration]);

  const reset = async () => {
    setCount(0);
    // Resetting the countdown timer to 55 minutes
    countdownLeftRef.current = initialDuration;
    // Calling sessionTouch and keeping alive the session whenever user clicks on keep me signed in
    keepAliveSession();
    setShowWarning(false);
  };

  if (!showWarning) return null;

  return (
    <Modal>
      <div className="session-timer_wrapper">
        <h1 className="session-timer_title">
          <Trans>Hey There!</Trans>
        </h1>
        <p>
          <Trans>
            Your session is about to expire. <br />
            Do you want to stay signed in?
          </Trans>
        </p>
        <div className="session-timer_actions">
          <Button className="session-timer_button" onClick={reset}>
            <Trans>Yes, Keep Me Signed In!</Trans>
          </Button>
          <Button
            className="session-timer_button"
            displayStyle="tertiary-outlined"
            onClick={logout}
          >
            <Trans>No, sign me out</Trans>
          </Button>
        </div>
      </div>
    </Modal>
  );
}
